/**
 * Audio cache using IndexedDB for storing audio files locally
 * This minimizes network requests by caching audio files after first download
 */

const DB_NAME = 'songkit-audio-cache';
const STORE_NAME = 'audio-files';
const DB_VERSION = 1;

interface CachedAudio {
  url: string; // S3 URL as key
  blob: Blob;
  cachedAt: number;
  expiresAt: number;
}

class AudioCache {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  /**
   * Initialize IndexedDB
   */
  private async init(): Promise<void> {
    if (this.db) {
      return;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('[AudioCache] Error opening IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('[AudioCache] IndexedDB initialized');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'url' });
          objectStore.createIndex('expiresAt', 'expiresAt', { unique: false });
          console.log('[AudioCache] Created object store');
        }
      };
    });

    return this.initPromise;
  }

  /**
   * Get cached audio blob by URL
   */
  async get(url: string): Promise<Blob | null> {
    await this.init();

    if (!this.db) {
      return null;
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(url);

      request.onsuccess = () => {
        const cached = request.result as CachedAudio | undefined;

        if (!cached) {
          resolve(null);
          return;
        }

        // Check if expired
        if (cached.expiresAt < Date.now()) {
          console.log('[AudioCache] Cached audio expired:', url);
          this.delete(url); // Clean up expired entry
          resolve(null);
          return;
        }

        console.log('[AudioCache] Cache hit for:', url);
        resolve(cached.blob);
      };

      request.onerror = () => {
        console.error('[AudioCache] Error getting cached audio:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Store audio blob in cache
   */
  async set(url: string, blob: Blob, expiresInHours: number = 24): Promise<void> {
    await this.init();

    if (!this.db) {
      return;
    }

    const cachedAudio: CachedAudio = {
      url,
      blob,
      cachedAt: Date.now(),
      expiresAt: Date.now() + (expiresInHours * 60 * 60 * 1000),
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(cachedAudio);

      request.onsuccess = () => {
        console.log('[AudioCache] Cached audio:', url);
        resolve();
      };

      request.onerror = () => {
        console.error('[AudioCache] Error caching audio:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Delete cached audio by URL
   */
  async delete(url: string): Promise<void> {
    await this.init();

    if (!this.db) {
      return;
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(url);

      request.onsuccess = () => {
        console.log('[AudioCache] Deleted cached audio:', url);
        resolve();
      };

      request.onerror = () => {
        console.error('[AudioCache] Error deleting cached audio:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Clear all cached audio
   */
  async clear(): Promise<void> {
    await this.init();

    if (!this.db) {
      return;
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => {
        console.log('[AudioCache] Cleared all cached audio');
        resolve();
      };

      request.onerror = () => {
        console.error('[AudioCache] Error clearing cache:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Get cache size in bytes
   */
  async getSize(): Promise<number> {
    await this.init();

    if (!this.db) {
      return 0;
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        const items = request.result as CachedAudio[];
        const totalSize = items.reduce((sum, item) => sum + item.blob.size, 0);
        resolve(totalSize);
      };

      request.onerror = () => {
        console.error('[AudioCache] Error getting cache size:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Get count of cached items
   */
  async getCount(): Promise<number> {
    await this.init();

    if (!this.db) {
      return 0;
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.count();

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        console.error('[AudioCache] Error getting cache count:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Clean up expired entries
   */
  async cleanup(): Promise<void> {
    await this.init();

    if (!this.db) {
      return;
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('expiresAt');
      const now = Date.now();

      const request = index.openCursor();

      request.onsuccess = () => {
        const cursor = request.result;

        if (cursor) {
          const cached = cursor.value as CachedAudio;

          if (cached.expiresAt < now) {
            cursor.delete();
            console.log('[AudioCache] Cleaned up expired audio:', cached.url);
          }

          cursor.continue();
        } else {
          resolve();
        }
      };

      request.onerror = () => {
        console.error('[AudioCache] Error during cleanup:', request.error);
        reject(request.error);
      };
    });
  }
}

// Export singleton instance
export const audioCache = new AudioCache();

// Run cleanup every hour
if (typeof window !== 'undefined') {
  setInterval(() => {
    audioCache.cleanup();
  }, 60 * 60 * 1000);
}
